import usePageSEO from '../hooks/usePageSEO';

import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  FileText, 
  RefreshCw, 
  Calendar, 
  Shield, 
  Lock, 
  Clock, 
  CheckCircle2,
  ArrowRight,
  Database,
  Cloud,
  CreditCard,
  BarChart3,
  Zap,
  Eye,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import HeroAnimation from '../components/HeroAnimation';
import Footer from '../components/Footer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

// Integration logos data
const integrations = [
  { name: 'SAP', icon: Database },
  { name: 'Oracle', icon: Database },
  { name: 'NetSuite', icon: Cloud },
  { name: 'QuickBooks', icon: BarChart3 },
  { name: 'Xero', icon: BarChart3 },
  { name: 'Sage', icon: Database },
  { name: 'Workday', icon: Cloud },
  { name: 'Microsoft Dynamics', icon: Database },
  { name: 'Stripe', icon: CreditCard },
  { name: 'Plaid', icon: CreditCard },
  { name: 'Expensify', icon: FileText },
];

// Features data
const features = [
  {
    title: 'Accounts Payable',
    description: 'Automate invoice ingestion, three-way matching, and payment scheduling. Handles exceptions with reasoning you can audit.',
    icon: FileText,
    points: ['Invoice extraction from any format', 'PO and receipt matching', 'Payment timing optimization', 'Variance detection and resolution'],
    colSpan: 'md:col-span-2',
    stat: '90%',
    statLabel: 'faster processing'
  },
  {
    title: 'Bank Reconciliation',
    description: 'Continuous matching across accounts and entities. Flags discrepancies before they compound.',
    icon: RefreshCw,
    points: ['Real-time transaction matching', 'Multi-entity consolidation', 'Exception categorization', 'Audit-ready documentation'],
    colSpan: 'md:col-span-1',
    stat: '99.8%',
    statLabel: 'accuracy rate'
  },
  {
    title: 'Financial Close',
    description: 'Replaces manual preparation with systematic verification. Reduces close time from days to hours.',
    icon: Calendar,
    points: ['Automated journal entries', 'Intercompany eliminations', 'Variance analysis', 'Compliance verification'],
    colSpan: 'md:col-span-3',
    stat: '5x',
    statLabel: 'faster close'
  },
];

// Process steps
const processSteps = [
  {
    number: '01',
    title: 'Connect',
    description: 'Authenticate across legacy and modern systems. Operate directly in portals without APIs. Extract structured data from unstructured documents.',
  },
  {
    number: '02',
    title: 'Act',
    description: 'Reconcile across invoices, statements, and ledgers. Complete and submit forms programmatically. Trigger downstream workflows on condition.',
  },
  {
    number: '03',
    title: 'Audit',
    description: 'Capture every action with screen recordings. Insert human review at configurable checkpoints. Generate regulator-ready audit trails.',
  },
];

// Security badges
const securityBadges = [
  { value: 'SOC 2 Type II', label: 'Certified', icon: Shield },
  { value: 'Bank-grade', label: 'Encryption', icon: Lock },
  { value: '99.99%', label: 'Uptime SLA', icon: Clock },
  { value: 'GDPR', label: 'Compliant', icon: Eye },
];

// FAQ data
const faqs = [
  {
    question: 'How do browser agents work without APIs?',
    answer: 'Our AI agents operate directly in web portals just like human users. They authenticate with your credentials, navigate interfaces, extract data, and perform actions—all without requiring API integrations or changes to your existing systems.',
  },
  {
    question: 'Do I need to replace my existing finance systems?',
    answer: 'No. Plouton AI works on top of your existing stack—SAP, Oracle, NetSuite, QuickBooks, or any other system. We operate through the same interfaces your team uses today, requiring zero replacements or integrations.',
  },
  {
    question: 'What level of oversight do I have?',
    answer: 'Complete visibility and control. Every action is captured with screen recordings. You can configure human review checkpoints at any stage. All decisions include reasoning you can audit.',
  },
  {
    question: 'How do you handle unstructured documents?',
    answer: 'Our agents extract structured data from any document format—PDFs, scanned invoices, emails, spreadsheets. We use advanced AI to understand context and accurately parse information regardless of format.',
  },
  {
    question: 'Is this compliant with financial regulations?',
    answer: 'Yes. We\'re SOC 2 Type II certified with bank-grade encryption, GDPR compliance, and generate regulator-ready audit trails. Our security architecture is designed for enterprise finance requirements from day one.',
  },
  {
    question: 'How long does implementation take?',
    answer: 'Most teams are operational within days, not months. Since we work with your existing systems without requiring integrations, setup is primarily about configuring workflows and training the agents on your specific processes.',
  },
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const HomePage = () => {
  usePageSEO({
    title: 'Experience No-Code Finance Automation With Plouton AI',
    description: "Scale your daily tasks using our browser-based AI agents. Plouton AI's no-code financial automations manage your accounts payable and bank reconciliation.",
    path: '/'
  });

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context":"https://schema.org","@type":"SoftwareApplication","name":"Plouton AI","url":"https://plouton.co/","applicationCategory":["FinanceApplication","BusinessApplication"],"operatingSystem":"Web browser, Cloud-based","description":"Plouton AI provides browser-use AI agents for finance automation. It operates Accounts Payable, Bank Reconciliation, and Financial Close workflows exactly like human team members, requiring zero API integrations.","publisher":{"@type":"Organization","name":"Plouton Technology Labs, PTE. LTD.","url":"https://plouton.co/"},"featureList":["Accounts Payable Automation","Bank Reconciliation","Financial Close Automation","Browser-use AI agents","Zero API integration required","Automated invoice ingestion and three-way matching","SOC 2 Type II Certified"]}).replace(/<\/script/gi, '<\\/script') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"How do browser agents work without APIs?","acceptedAnswer":{"@type":"Answer","text":"Our AI agents operate directly in web portals just like human users. They authenticate with your credentials, navigate interfaces, extract data, and perform actions\u2014all without requiring API integrations or changes to your existing systems."}},{"@type":"Question","name":"Do I need to replace my existing finance systems?","acceptedAnswer":{"@type":"Answer","text":"No. Plouton AI works on top of your existing stack\u2014SAP, Oracle, NetSuite, QuickBooks, or any other system. We operate through the same interfaces your team uses today, requiring zero replacements or integrations."}},{"@type":"Question","name":"What level of oversight do I have?","acceptedAnswer":{"@type":"Answer","text":"Complete visibility and control. Every action is captured with screen recordings. You can configure human review checkpoints at any stage. All decisions include reasoning you can audit."}},{"@type":"Question","name":"How do you handle unstructured documents?","acceptedAnswer":{"@type":"Answer","text":"Our agents extract structured data from any document format\u2014PDFs, scanned invoices, emails, spreadsheets. We use advanced AI to understand context and accurately parse information regardless of format."}},{"@type":"Question","name":"Is this compliant with financial regulations?","acceptedAnswer":{"@type":"Answer","text":"Yes. We\u2019re SOC 2 Type II certified with bank-grade encryption, GDPR compliance, and generate regulator-ready audit trails. Our security architecture is designed for enterprise finance requirements from day one."}},{"@type":"Question","name":"How long does implementation take?","acceptedAnswer":{"@type":"Answer","text":"Most teams are operational within days, not months. Since we work with your existing systems without requiring integrations, setup is primarily about configuring workflows and training the agents on your specific processes."}}]}).replace(/<\/script/gi, '<\\/script') }} />
      {/* Hero Section */}
      <section className="relative pt-[120px] pb-16 sm:pt-[140px] sm:pb-20 lg:pt-[160px] lg:pb-32 hero-pattern overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text content */}
            <motion.div 
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              {/* Trust badge */}
              <motion.div 
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-sm font-medium text-blue-700 mb-8"
              >
                <Shield className="w-4 h-4" />
                <span>SOC 2 Type II Certified • Bank-grade Security</span>
              </motion.div>

              {/* Main headline */}
              <motion.h1 
                variants={fadeInUp}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900 mb-6"
                data-testid="hero-headline"
              >
                Automate <span className="text-blue-600">85%</span> of Finance Workflows{' '}
                <span className="text-orange-500">Without APIs</span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p 
                variants={fadeInUp}
                className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-10 max-w-xl"
                data-testid="hero-subheadline"
              >
                Browser-use AI agents that operate Accounts Payable, Bank Reconciliation, and Financial Close exactly like your best team members. Zero integration required.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg"
                    className="bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl transition-all duration-300 font-semibold rounded-lg px-8 py-6 text-base w-full sm:w-auto"
                    data-testid="hero-book-demo-button"
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-semibold rounded-lg px-8 py-6 text-base"
                  data-testid="hero-learn-more-button"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  See How It Works
                </Button>
              </motion.div>

              {/* Stats row */}
              <motion.div 
                variants={fadeInUp}
                className="grid grid-cols-3 gap-8 max-w-xl"
              >
                <div className="text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading">85%</div>
                  <div className="text-sm text-slate-500">Tasks Automated</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading">5x</div>
                  <div className="text-sm text-slate-500">Faster Close</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading">0</div>
                  <div className="text-sm text-slate-500">APIs Needed</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Animated graphic */}
            <div className="hidden lg:block relative">
              <HeroAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* Product Preview Section */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4 block">
              Product
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Browser Agents for Finance Operations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Automate repetitive Finance workflows without changing your existing systems. Built for Enterprise-grade security, full auditability, and seamless scale.
            </p>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Glow effect behind the mockup */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-transparent to-orange-500/20 blur-3xl transform scale-110 -z-10" />
            
            {/* Browser frame */}
            <div className="relative bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-800">
              {/* Browser header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/50 border-b border-slate-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 bg-slate-700/50 rounded-lg text-xs text-slate-400 font-mono">
                    app.plouton.co
                  </div>
                </div>
              </div>
              
              {/* Dashboard screenshot */}
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/p3z673o2_mockup%20%281%29.png"
                  alt="Plouton AI Dashboard - Automated Invoice Processing and Three-Way Matching"
                  className="w-full h-auto"
                  width={1500}
                  height={700}
                  data-testid="product-dashboard-mockup"
                />
                {/* Subtle overlay gradient at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-900/50 to-transparent" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Integrations Banner */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mb-8">
          <p className="text-center text-slate-500 font-medium">
            Works directly in your existing portals — no integrations required
          </p>
        </div>
        <div className="marquee-container">
          <Marquee speed={40} gradient gradientColor="#F8FAFC" gradientWidth={100}>
            {integrations.map((integration) => (
              <div 
                key={integration.name}
                className="flex items-center gap-2 mx-4 px-5 py-2.5 bg-white border border-slate-200 rounded-full shadow-sm"
              >
                <integration.icon className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{integration.name}</span>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 sm:py-32" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4 block">
              How It Works
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              A continuous control loop for finance operations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our AI agents work around the clock, operating your finance workflows with full transparency and auditability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                className="relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 hover:shadow-lg hover:border-slate-300 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold text-lg font-heading">
                    {step.number}
                  </span>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">{step.title}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-16 sm:py-24 lg:py-32 bg-slate-50" id="features">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4 block">
              Use Cases
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Built for work that scales poorly
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Finance operations that require judgment, not just execution. Tasks where errors are expensive and volume is unforgiving.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className={`bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 hover:shadow-lg hover:border-slate-300 transition-all duration-300 ${feature.colSpan}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100">
                      <feature.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900">{feature.title}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-500 font-heading">{feature.stat}</div>
                    <div className="text-xs text-slate-500">{feature.statLabel}</div>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.points.map((point) => (
                    <li key={point} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-500 mb-6">
              Each workflow is observable. Every decision includes reasoning.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transition-all duration-300 font-semibold rounded-lg"
                data-testid="features-book-demo-button"
              >
                Book A Live Demo Session
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Forbes 30 Under 30 Recognition */}
      <section className="py-16 sm:py-24 bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[180px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-orange-500/5 rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col md:flex-row items-center gap-10 md:gap-14">
              {/* Photo */}
              <div className="flex-shrink-0 w-full md:w-auto">
                <a 
                  href="https://www.forbes.com/profile/plouton-ai/?list=30under30-asia-finance-venture-capital/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="relative rounded-2xl overflow-hidden border border-slate-800 max-w-md mx-auto md:w-80 group">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/pekz61al_Screenshot%202026-06-12%20at%208.40.11%E2%80%AFPM.png"
                      alt="Furqan Kidwai and Sarfaraz Hussain - Forbes 30 Under 30 Asia 2026 Finance and Venture Capital"
                      className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                      width={800}
                      height={600}
                      loading="lazy"
                    />
                  </div>
                </a>
              </div>

              {/* Text */}
              <div className="text-center md:text-left">
                <a 
                  href="https://www.forbes.com/profile/plouton-ai/?list=30under30-asia-finance-venture-capital/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-5 py-2.5 mb-6 hover:border-white/20 transition-colors"
                >
                  <span className="font-bold text-lg text-white tracking-tight" style={{ fontFamily: "'Georgia', serif" }}>Forbes</span>
                  <span className="w-px h-5 bg-white/20" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-red-400 leading-tight">30 Under 30<br/>Asia 2026</span>
                </a>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight">
                  Recognized by Forbes 30 Under 30 Asia
                </h2>
                <p className="text-slate-400 leading-relaxed mb-5 max-w-lg">
                  Plouton AI co-founders <span className="text-white font-medium">Furqan Kidwai</span> and <span className="text-white font-medium">Sarfaraz Hussain</span> were 
                  named to the Forbes 30 Under 30 Asia 2026 list in Finance &amp; Venture Capital for building browser-native AI agents 
                  that automate finance operations without APIs or system changes.
                </p>
                <a 
                  href="https://www.forbes.com/profile/plouton-ai/?list=30under30-asia-finance-venture-capital/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Read the Forbes profile
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company + Security Section */}
      <section className="py-16 sm:py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
            {/* Left: Company */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5 block">
                Company
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4 leading-tight">
                Built with the support of world-class operators.
              </h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-8 max-w-lg">
                Plouton AI is being built with the support of leading startup and infrastructure ecosystems. Our work is informed by operators, engineers, and advisors from companies shaping modern finance and developer tooling.
              </p>

              <div>
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-slate-500 mb-4 block">
                  Built with support from
                </span>
                <div className="flex items-center gap-5 sm:gap-8 flex-wrap">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/cgn02hi3_antler-1280x720-1.png" 
                    alt="Antler - Plouton AI Venture Partner" 
                    className="h-10 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
                    width={124}
                    height={70}
                    loading="lazy"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/v7zbaoym_Google_Cloud_logo.svg.png" 
                    alt="Google Cloud Platform - Plouton AI Infrastructure" 
                    className="h-8 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
                    width={361}
                    height={56}
                    loading="lazy"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/5fk02w1c_GitHub-logo.jpg" 
                    alt="GitHub - Plouton AI Development Platform" 
                    className="h-8 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
                    width={97}
                    height={56}
                    loading="lazy"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/50xgrxzv_images%20%281%29.png" 
                    alt="Intercom - Plouton AI Customer Support Integration" 
                    className="h-8 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
                    width={56}
                    height={56}
                    loading="lazy"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/5gc4sx5f_images.png" 
                    alt="OpenAI - Plouton AI Technology Partner" 
                    className="h-8 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
                    width={56}
                    height={56}
                    loading="lazy"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right: Security */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "200px" }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5 block">
                Security
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4 leading-tight">
                Enterprise-grade from day one.
              </h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-8 max-w-lg">
                Your financial data requires the highest standard of protection. We built Plouton AI to meet requirements before you have to ask.
              </p>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {securityBadges.map((badge, index) => (
                  <motion.div
                    key={badge.label}
                    className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 flex items-start justify-between gap-3 hover:border-slate-300 hover:shadow-sm transition-all"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "100px" }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                  >
                    <div>
                      <div className="text-base font-bold text-slate-900 font-heading">{badge.value}</div>
                      <div className="text-sm text-slate-400 mt-0.5">{badge.label}</div>
                    </div>
                    <badge.icon className="w-7 h-7 text-slate-300 flex-shrink-0 mt-0.5" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4 block">
              FAQ
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Common questions
            </h2>
            <p className="text-lg text-slate-600">
              If you don't find your answer here,{' '}
              <a href="mailto:hello@plouton.co" className="text-blue-600 hover:underline">
                reach out directly
              </a>.
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.question} 
                value={faq.question}
                className="bg-white border border-slate-200 rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              Ready to automate your finance workflows?
            </h2>
            <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
              We're onboarding companies with complex finance operations. Book a demo and we'll show you how Plouton AI can transform your workflows.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg"
                className="bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl transition-all duration-300 font-semibold rounded-lg px-10 py-6 text-lg"
                data-testid="cta-book-demo-button"
              >
                Book a Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <p className="text-sm text-slate-500 mt-6">
              No sales pressure. Just a product walkthrough.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;